"use client"

import { useState } from "react"

import { Button } from "@/components/ui/button"
import { EditableText } from "@/components/ui/editable-text"
import { FileUpload } from "@/components/ui/file-upload"
import { HintText, Label } from "@/components/ui/input"
import { ProseField } from "@/components/ui/prose-editor"
import { SectionFooter } from "@/components/ui/section-footer"
import { Toggle } from "@/components/ui/toggle"
import type { Tag } from "@/types"

import { ClaimTrackBoard } from "./claim-track-board"
import type { ClaimTrack } from "./claim-track-card"
import type { PlaylistFormData } from "./playlist-form"

interface OwnerEditManuscriptProps {
  data: PlaylistFormData
  setData: (next: PlaylistFormData) => void
  errors: Record<string, string | undefined>
  processing: boolean
  /** Initial HTML for the description editor (uncontrolled). */
  initialDescription: string
  /** Existing cover image URL to seed the preview. */
  initialCoverUrl?: string | null
  onSubmit: (e: React.FormEvent) => void
  onCancel: () => void
  onCoverRemovedChange?: (removed: boolean) => void
  /** The playlist identity + its tracks + tag options for the interactive board. */
  playlist: { id: number; slug: string | null }
  tracks: ClaimTrack[]
  availableTags: Tag[]
  /** Where a live track mutation returns — the owner or the admin editor. */
  returnMode: "edit" | "admin_edit"
}

/**
 * The owner playlist editor as an edit-surface manuscript: the title and
 * description are edited in place in their published voice (EditableText +
 * ProseField), and the tracks are a direct-manipulation board (paste-a-link to
 * add, drag to reorder, inline note, forgiving remove) that persists live. The
 * words and the cover/visibility meta still batch into an explicit Save — the
 * tracks are the only thing that saves as you go. See docs/design/edit-surfaces.md.
 */
export function OwnerEditManuscript({
  data,
  setData,
  errors,
  processing,
  initialDescription,
  initialCoverUrl = null,
  onSubmit,
  onCancel,
  onCoverRemovedChange,
  playlist,
  tracks,
  availableTags,
  returnMode,
}: OwnerEditManuscriptProps) {
  const [coverPreview, setCoverPreview] = useState<string | null>(
    initialCoverUrl,
  )

  const handleCoverSelect = (files: FileList) => {
    const file = files[0]
    if (!file) return
    onCoverRemovedChange?.(false)
    setData({ ...data, cover_image: file })
    const reader = new FileReader()
    reader.onload = (e) => setCoverPreview(e.target?.result as string)
    reader.readAsDataURL(file)
  }

  const handleRemoveCover = () => {
    onCoverRemovedChange?.(true)
    setData({ ...data, cover_image: null })
    setCoverPreview(null)
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-8">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5 lg:gap-8">
        {/* The words, edited in place in their published voice. */}
        <div className="flex flex-col gap-4 lg:col-span-3">
          <div className="flex flex-col gap-1">
            <EditableText
              presentation="display"
              ariaLabel="Playlist title"
              value={data.title}
              onChange={(v) => setData({ ...data, title: v })}
              placeholder="Name your playlist"
              isInvalid={!!errors["playlist.title"]}
            />
            {errors["playlist.title"] && (
              <p className="text-error-primary text-sm">
                {errors["playlist.title"]}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <ProseField
              ariaLabel="Playlist description"
              initialContent={initialDescription}
              onUpdate={(html) => setData({ ...data, description: html })}
              placeholder="Say a little about why these tracks belong together…"
              isInvalid={!!errors["playlist.description"]}
            />
            {errors["playlist.description"] && (
              <p className="text-error-primary text-sm">
                {errors["playlist.description"]}
              </p>
            )}
          </div>
        </div>

        {/* Cover + visibility — a compact meta card the owner still manages. */}
        <div className="lg:col-span-2">
          <div className="border-secondary bg-primary flex flex-col gap-5 rounded-2xl border p-5 lg:sticky lg:top-6">
            <div className="flex flex-col gap-1.5">
              <Label>Cover image</Label>
              {coverPreview ? (
                <div className="relative overflow-hidden rounded-2xl">
                  <img
                    src={coverPreview}
                    alt="Cover preview"
                    className="aspect-video w-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition hover:opacity-100">
                    <Button
                      type="button"
                      color="secondary"
                      size="sm"
                      onClick={handleRemoveCover}
                    >
                      Change
                    </Button>
                  </div>
                </div>
              ) : (
                <FileUpload.Root>
                  <FileUpload.DropZone
                    accept="image/*"
                    allowsMultiple={false}
                    hint="PNG, JPG, GIF or WebP (max. 5MB)"
                    maxSize={5 * 1024 * 1024}
                    onDropFiles={handleCoverSelect}
                  />
                </FileUpload.Root>
              )}
              {errors["playlist.cover_image"] && (
                <HintText isInvalid>{errors["playlist.cover_image"]}</HintText>
              )}
            </div>

            <Toggle
              isSelected={data.published}
              onChange={(isSelected) =>
                setData({ ...data, published: isSelected })
              }
              size="md"
              hint={
                data.published
                  ? "This playlist is published and visible to everyone."
                  : "This playlist is unpublished and only visible to you."
              }
              label={data.published ? "Published" : "Unpublished"}
              slim
            />
          </div>
        </div>
      </div>

      {/* The tracks — a live direct-manipulation board (persists as you go). */}
      <ClaimTrackBoard
        claim={null}
        playlist={playlist}
        tracks={tracks}
        availableTags={availableTags}
        persistParams={{ return: returnMode }}
        heading="Tracks"
      />

      <SectionFooter.Root>
        <SectionFooter.Actions>
          <Button type="button" color="secondary" size="md" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            size="md"
            isDisabled={processing}
            isLoading={processing}
          >
            Save Changes
          </Button>
        </SectionFooter.Actions>
      </SectionFooter.Root>
    </form>
  )
}
